import { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../../../config";
import i18n from "i18n";

const API_URL = config.TRAFFIC;

function formatTime(value) {
  if (value == null || isNaN(value)) return "";

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const [minutePart, secondPart] = absValue.toString().split(".");
  const totalMinutes = parseInt(minutePart, 10);
  const seconds = secondPart ? parseInt(secondPart.padEnd(2, "0"), 10) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = isNegative ? "-" : "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0 || hours > 0) result += `${minutes} min `;
  result += `${seconds} sec`;

  return result.trim();
}

function transformAPI(apiData) {
  return apiData
    .map((data) => [
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkSpeed.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: `${Math.round(data.avg_speed_current)} km/h`,
            status:
              data.avg_speed_current > data.avg_speed_typical
                ? "pass"
                : "notPass",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: `${Math.round(data.avg_speed_typical)} km/h`,
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: data.avg_speed_forecasted
              ? `${Math.round(data.avg_speed_forecasted)} km/h`
              : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkSpeed.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkDelay.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.delay"),
            value: formatTime(data.delay),
            // status: data.delay > 0 ? "pass" : "notPass",
            status: "notPass", // always red
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: data.delay_forecasted
              ? formatTime(data.delay_forecasted)
              : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkDelay.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkTravelTime.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: formatTime(data.tt_current),
            status: data.tt_typical > data.tt_current ? "pass" : "notPass",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: formatTime(data.tt_typical),
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: data.tt_forecasted ? formatTime(data.tt_forecasted) : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.avgNetworkTravelTime.description"),
      },
      {
        name: i18n.t("trafficMonitoring.monitoring.metrics.congestion.name"),
        values: [
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.current"),
            value: `${data.congestion_current} %`,
            // status:
            //   data.congestion_current > data.congestion_typical
            //     ? "notPass"
            //     : "pass",
            status: "notPass", // always red
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.typical"),
            value: `${Math.round(data.congestion_typical)} %`,
            status: "neutral",
          },
          {
            label: i18n.t("trafficMonitoring.monitoring.metrics.labels.forecasted"),
            value: data.congestion_forecasted
              ? `${Math.round(data.congestion_forecasted)} %`
              : "",
            status: "neutral",
          },
        ],
        description: i18n.t("trafficMonitoring.monitoring.metrics.congestion.description"),
      },
    ])
    .flat();
}

const useNetworkData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lastUpdated, setLastUpdated] = useState(null);

  const isFirstLoad = useRef(true);
  const intervalId = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const start = new Date();
        // console.log(`[${start.toLocaleTimeString()}] Fetching network data…`);

        const response = await axios.get(
          `${API_URL}/latest_traffic_data_per_network`,
          {
            headers: { "Accept-Profile": "traffic_monitoring" },
          }
        );

        const formatted = transformAPI(response.data);
        setData(formatted);

        // log the formatted data
        // console.log(
        //   `[${new Date().toLocaleTimeString()}] 📊 Received Network data:`,
        //   formatted
        // );

        const end = new Date();
        // console.log(
        //   `[${end.toLocaleTimeString()}] Data received (took ${(
        //     (end - start) /
        //     1000
        //   ).toFixed(2)}s)`
        // );
        setLastUpdated(end);
      } catch (err) {
        console.error("Error fetching traffic data:", err);
      } finally {
        if (isFirstLoad.current) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    };

    fetchData();

    intervalId.current = setInterval(fetchData, 60000);
    return () => {
      clearInterval(intervalId.current);
    };
  }, []);

  return { data, loading };
};

export default useNetworkData;
